/**
 * Photos: resize to 2,400 px, re-encode (which drops EXIF and the location), then upload through the contract
 * (create, PUT bytes, complete). Uploads start the moment photos are picked and report progress per photo
 * (docs/52 S51). Anything not finished when the job is sent waits in the outbox (lib/outbox.ts).
 */
import { jobs, type PhotoKind } from '@ff/contracts';
const { UPLOAD_LIMITS } = jobs;
import { ApiError, getTransport, pathFor } from './api.ts';
import { send } from './data.ts';
import { fitLongEdge, readGps, type Gps } from './exif.ts';

export interface Processed {
  blob: Blob;
  contentType: string;
  width: number | null;
  height: number | null;
  gps: Gps | null;
  /** False when the browser couldn't decode the file (HEIC in Chrome): it goes up as is and the server strips it. */
  reencoded: boolean;
}

export async function processPhoto(file: File): Promise<Processed> {
  const head = await file.slice(0, 256 * 1024).arrayBuffer();
  const gps = readGps(head);
  try {
    const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
    const { w, h } = fitLongEdge(bmp.width, bmp.height, UPLOAD_LIMITS.photoLongEdgePx);
    const canvas = typeof OffscreenCanvas !== 'undefined' ? new OffscreenCanvas(w, h) : Object.assign(document.createElement('canvas'), { width: w, height: h });
    const ctx = canvas.getContext('2d') as CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null;
    if (!ctx) throw new Error('no 2d context');
    ctx.drawImage(bmp, 0, 0, w, h);
    bmp.close();
    const blob =
      canvas instanceof HTMLCanvasElement
        ? await new Promise<Blob>((res, rej) => canvas.toBlob((b) => (b ? res(b) : rej(new Error('encode failed'))), 'image/jpeg', 0.86))
        : await (canvas as OffscreenCanvas).convertToBlob({ type: 'image/jpeg', quality: 0.86 });
    return { blob, contentType: 'image/jpeg', width: w, height: h, gps, reencoded: true };
  } catch {
    return { blob: file, contentType: file.type || 'image/heic', width: null, height: null, gps, reencoded: false };
  }
}

function putBytes(path: string, blob: Blob, onProgress: (f: number) => void, signal?: AbortSignal): Promise<void> {
  const t = getTransport();
  // Real network: XHR gives upload progress. Mock transport: one fetch, progress in two steps.
  if ((t as { mock?: boolean }).mock !== true && typeof XMLHttpRequest !== 'undefined') {
    return new Promise((resolve, reject) => {
      const x = new XMLHttpRequest();
      x.open('PUT', path);
      x.setRequestHeader('X-FF-Req', '1');
      x.setRequestHeader('content-type', blob.type || 'application/octet-stream');
      x.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
      x.onload = () => (x.status >= 200 && x.status < 300 ? resolve() : reject(new ApiError('internal', x.status, 'upload failed')));
      x.onerror = () => reject(new ApiError('offline', 0, 'offline'));
      signal?.addEventListener('abort', () => x.abort());
      x.send(blob);
    });
  }
  onProgress(0.35);
  return t(path, { method: 'PUT', body: blob, headers: { 'X-FF-Req': '1', 'content-type': blob.type }, signal }).then((r) => {
    if (!r.ok) throw new ApiError('internal', r.status, 'upload failed');
    onProgress(1);
  }, () => {
    throw new ApiError('offline', 0, 'offline');
  });
}

/** What the client says a photo shows, sent as the upload finishes (the library and the get live step ask). */
export interface PhotoHint {
  kind?: PhotoKind;
  serviceId?: string;
  title?: string;
}

export async function uploadBlob(
  blob: Blob,
  contentType: string,
  kind: 'photo' | 'voice' | 'document',
  purpose: 'job' | 'insurance' | 'request_attachment' | 'bulk_sheet',
  onProgress: (f: number) => void,
  signal?: AbortSignal,
  hint: PhotoHint = {},
): Promise<string> {
  const created = await send('uploadCreate', { body: { kind, purpose, contentType, bytes: blob.size } });
  await putBytes(created.putPath || pathFor('uploadPut', { id: created.uploadId }), blob, onProgress, signal);
  await send('uploadComplete', { params: { id: created.uploadId }, body: kind === 'photo' ? ({ ...(hint.kind ? { kind: hint.kind } : {}), ...(hint.serviceId ? { serviceId: hint.serviceId } : {}), ...(hint.title ? { title: hint.title } : {}) } as never) : {} });
  onProgress(1);
  return created.uploadId;
}
